import { useAvailablePromoCodesList, useDefaultPaymentPlanId } from '@if/core'
import { EFinancingProgram, EProvince } from '@if/core/types'
import {
  BaseWorksheetFormConfig,
  buildBaseWorksheetFormConfig,
  useResolvedFinancingConfig,
} from '@src/components/WorksheetForm'
import { useBaseWorksheetFormInitialValues } from '@src/components/WorksheetForm/BaseWorksheetForm.schema'
import { useCreditRslaTermsByPlanName } from '@src/containers/EditWorksheet/editWorksheet-hooks'
import { useGetMerchantById } from '@src/data/api/merchant/merchant-api'
import { Constants } from '@src/data/types'
import { ETermMax } from '@src/data/types/constants'
import { BaseWorksheetDto } from '@src/data/types/BaseWorksheetSchema'
import { NormCreditDecision } from '@src/data/types/CreditDecisionSchema'
import { Merchant } from '@src/data/types/Merchant'
import { nanoid } from 'nanoid'
import { useMemo } from 'react'
import { CalculatorValues, CalculatorSchema, useCalculatorForm } from '../calculatorSchema'

export interface UseCalculatorFormSetupProps {
  creditApplicationId: string | null
  financingProgramId: EFinancingProgram | null
  merchantId: string | null
  expiresOn: Date | string | null
  createdOn: Date | null
  decisionInterestRate: number | null
  maxAmountFinanced: number | null
  maxTermDuration: number | null
  maxPmtAmount?: number
  hasCoApplicant: boolean
  stateIso: EProvince | null
  normsCreditDecision: NormCreditDecision | null
  // Single-payment applications (e.g. MechanicalWarranty) force the payment frequency to Single.
  isSinglePayment?: boolean
  // CreditRsla restricts terms by the applicant's decision plan.
  creditRslaPlanName?: string | null
}

export interface CalculatorFormSetup {
  config: BaseWorksheetFormConfig | null
  interestRate: number
  merchant: Merchant | null
  availablePromoCodes: Set<string>
}

// Data prep only — the form schema is built separately in useCalculatorFormFields, gated on config != null.
export const useCalculatorFormSetup = ({
  creditApplicationId,
  financingProgramId,
  merchantId,
  expiresOn,
  createdOn,
  decisionInterestRate,
  maxAmountFinanced,
  maxTermDuration,
  maxPmtAmount,
  hasCoApplicant,
  stateIso,
  normsCreditDecision,
  isSinglePayment = false,
  creditRslaPlanName,
}: UseCalculatorFormSetupProps): CalculatorFormSetup => {
  const [merchant] = useGetMerchantById(merchantId ?? undefined)

  const creditRslaTerms = useCreditRslaTermsByPlanName(creditRslaPlanName)
  const supportedTermsOverride =
    financingProgramId === EFinancingProgram.CreditRsla ? (creditRslaTerms as ETermMax[]) : undefined

  const programConfig =
    (financingProgramId && Constants.financingProgramConfigs[financingProgramId]) ||
    Constants.financingProgramConfigs.default

  const { financingConfig } = useResolvedFinancingConfig(financingProgramId ?? undefined, merchantId ?? undefined)

  const interestRate = decisionInterestRate ?? programConfig.minInterestRate

  const availablePromoCodes = useAvailablePromoCodesList(
    merchant?.planPromoRules,
    normsCreditDecision,
    // Sentinel: when financingProgramId is null, config is null below so this set never reaches a consumer.
    financingProgramId ?? EFinancingProgram.Personal,
  )

  const config = useMemo<BaseWorksheetFormConfig | null>(() => {
    if (!financingProgramId || !stateIso || !financingConfig || !createdOn || !expiresOn) {
      return null
    }
    return buildBaseWorksheetFormConfig({
      financingProgramId,
      creditApplicationId: creditApplicationId ?? nanoid(),
      merchantId: merchant?.id ?? '',
      financingConfig,
      stateIso,
      hasCoApplicant,
      interestRate,
      maxPmtAmount,
      createdOn,
      expiresOn,
      merchant,
      normsCreditDecision,
      maxAmountFinanced,
      maxTermDuration,
      isSinglePayment,
      availablePromoCodes,
      supportedTermsOverride,
    })
  }, [
    financingProgramId,
    creditApplicationId,
    merchant,
    financingConfig,
    stateIso,
    hasCoApplicant,
    interestRate,
    maxPmtAmount,
    createdOn,
    expiresOn,
    normsCreditDecision,
    maxAmountFinanced,
    maxTermDuration,
    isSinglePayment,
    availablePromoCodes,
    supportedTermsOverride,
  ])

  return {
    config,
    interestRate,
    merchant,
    availablePromoCodes,
  }
}

export interface UseCalculatorFormFieldsArgs {
  config: BaseWorksheetFormConfig
  availablePromoCodes: Set<string>
  worksheet: BaseWorksheetDto | null
  requestedLoanAmount: number | null
}

export interface CalculatorFormFields {
  schema: CalculatorSchema
  defaultValues: CalculatorValues | undefined
}

// Builds the schema and initial values; call only with a non-null config.
export const useCalculatorFormFields = ({
  config,
  availablePromoCodes,
  worksheet,
  requestedLoanAmount,
}: UseCalculatorFormFieldsArgs): CalculatorFormFields => {
  const defaultPaymentPlanId = useDefaultPaymentPlanId(config.availablePaymentPlans, availablePromoCodes)
  const { schema, defaultValues } = useCalculatorForm(config)
  const initialValues = useBaseWorksheetFormInitialValues(
    defaultValues,
    worksheet,
    requestedLoanAmount,
    defaultPaymentPlanId,
  )
  return { schema, defaultValues: initialValues }
}
